import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../shared/Sidebar';
import securityIcon from '../../images/security-cameras.png';
import { ROLES } from '../../utils/helpers';
import { getItem } from '../../utils/authStorage';
import { useTheme } from '../../contexts/ThemeContext';
import './ForeignObjectDetectionStream.css';
import LoadingSpinner from '../shared/LoadingSpinner';
import WhepVideoPlayer from '../shared/WhepVideoPlayer';
import { ENDPOINTS } from '../../utils/appConfig';

function ForeignObjectDetectionStream() {
  const location = useLocation();
  const { flightId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [flightData, setFlightData] = useState(location.state?.flightData || null);
  const [showLeftMenu, setShowLeftMenu] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const username = getItem('username');
  const rawRole = getItem('role');
  const role = (rawRole || 'operator').toLowerCase();
  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem('role') || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  const handleSignOut = useCallback(() => {
    localStorage.clear();
    navigate('/login');
  }, [navigate]);

  const streamEndPollRef = useRef(null);

  const handleStreamEnd = useCallback(() => {
    if (!flightId) return;
    const url = `${process.env.REACT_APP_API_BASE_URL || window.location.origin}/command-center/cc-alerts/foreign-object-detection/${flightId}/stream/`;
    const token = getItem('accessToken');
    let attempts = 0;
    const maxAttempts = 30;
    const poll = () => {
      attempts++;
      fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.is_active === false) {
            sessionStorage.removeItem('fodStreamActive');
            navigate('/drone-runway');
          } else if (attempts < maxAttempts) {
            streamEndPollRef.current = setTimeout(poll, 2000);
          }
        })
        .catch(() => {
          if (attempts < maxAttempts) {
            streamEndPollRef.current = setTimeout(poll, 2000);
          }
        });
    };
    poll();
  }, [flightId, navigate]);

  useEffect(() => {
    return () => {
      if (streamEndPollRef.current) {
        clearTimeout(streamEndPollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!flightId) return;
    const token = getItem('accessToken');
    const streamUrl = `${process.env.REACT_APP_API_BASE_URL || window.location.origin}/command-center/cc-alerts/foreign-object-detection/${flightId}/stream/`;
    const interval = setInterval(() => {
      fetch(streamUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.is_active === false) {
            clearInterval(interval);
            sessionStorage.removeItem('fodStreamActive');
            navigate('/drone-runway');
          }
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [flightId, navigate]);

  useEffect(() => {
    if (flightId && !flightData) {
      const token = getItem('accessToken');
      const streamUrl = `${process.env.REACT_APP_API_BASE_URL || window.location.origin}/command-center/cc-alerts/foreign-object-detection/${flightId}/stream/`;
      fetch(streamUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setFlightData(prev => ({ ...prev, ...data, flight_id: flightId }));
          }
        })
        .catch(() => {});
    }
  }, [flightId, flightData]);

  useEffect(() => {
    if (flightData && (flightData.whep_url || flightData.edge_url)) {
      sessionStorage.setItem('fodStreamActive', 'true');
    }
  }, [flightData]);

  useEffect(() => {
    if (flightData?.drone_id) {
      const alertsUrl = ENDPOINTS.FOREIGN_OBJECT_ALERTS(flightData.drone_id) + `&drone_flight=${flightId}`;
      fetch(alertsUrl, {
        headers: { 'Authorization': `Bearer ${getItem('accessToken')}` },
      })
        .then(res => res.ok ? res.json() : [])
        .then(data => setAlerts(Array.isArray(data) ? data : data?.results || []))
        .catch(() => {});
    }
  }, [flightData?.drone_id, flightId]);

  if (!flightData) {
    return (
      <div style={{ padding: 24 }}>
        <h3>Drone Flight Stream</h3>
        <p>No flight was initialized. Please trigger the Drone Fly action from the Drone Runway page.</p>
      </div>
    );
  }

  const whepUrl = flightData.whep_url || flightData.edge_url || null;

  return (
    <div className={`alert-detail-wrapper-foreign-object-detection${showLeftMenu ? '' : ' collapsed'}`}>
      <Sidebar
        collapsed={!showLeftMenu}
        onToggleCollapse={() => setShowLeftMenu(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />
      <div className="alert-detail-header-foreign-object-detection">
        <div className="top-nav-bar d-flex align-items-center px-3 py-2 gap-2">
          <button
            className="btn-link-foreign-object-detection text-white p-0 icon-only"
            onClick={() => setShowLeftMenu(prev => !prev)}
            title={showLeftMenu ? 'Close Menu' : 'Open Menu'}
            aria-label="Toggle Sidebar"
          >
            <img src={securityIcon} alt="Toggle Sidebar" className="sidebar-toggle-icon-foreign-object-detection" />
          </button>
          <h1 className="alert-detail-header-title-foreign-object-detection">Drone Flight Live Stream</h1>
        </div>
      </div>
      <div className="alert-detail-content-foreign-object-detection">
        <div className="alertdetails-device-card-foreign-object-detection main-device-card-foreign-object-detection">
          <div className="alert-video-container-foreign-object-detection main-video single-device">
            {!whepUrl ? (
              <LoadingSpinner message="Loading stream…" />
            ) : (
              <WhepVideoPlayer edgeUrl={whepUrl} onStreamEnd={handleStreamEnd} />
            )}
          </div>
        </div>

        <div style={{ padding: '16px 0', color: '#ccc' }}>
          <p>Flight ID: <strong>{flightData.flight_id || flightId}</strong></p>
          <p>Drone ID: <strong>{flightData.drone_id || 'N/A'}</strong></p>
          {flightData.start_time && <p>Started: <strong>{flightData.start_time}</strong></p>}
        </div>

        {alerts.length > 0 && (
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ color: '#fff', marginBottom: 12 }}>Detection Alerts</h3>
            {alerts.map(alert => (
              <div key={alert.id || alert.alertId} style={{
                background: '#1a1a2e', borderRadius: 8, padding: '12px 16px',
                marginBottom: 8, border: '1px solid #333'
              }}>
                <p style={{ color: '#fff', margin: 0 }}>{alert.message || 'Alert'}</p>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                  fontSize: 12, marginTop: 4,
                  background: alert.severity === 'HIGH' ? '#ff4444' : alert.severity === 'MEDIUM' ? '#ff8800' : '#ffaa00',
                  color: '#fff'
                }}>{alert.severity || 'INFO'}</span>
                {alert.status && (
                  <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>{alert.status}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ForeignObjectDetectionStream;
